import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { clientPath } from '../lib/clientLink.js';
import Working from '../components/Working.jsx';
import { Field, Toggle, fmtDateTime } from '../components/FormBits.jsx';
import { SaveStatus, useSaveStatus } from '../components/SaveStatus.jsx';
import { GmbAuditView, BUCKET, OWNER_LABEL } from './GmbAudit.jsx';

// Session 10: agency GMB Audit for one client. Session 11: rendered as the
// GMB Audit tab of /admin/clients/:clientId/settings (?tab=gmb); the old
// /admin/clients/:clientId/gmb route redirects there.
//   Upload   title + PowerPoint (.pptx, required) + audit.json (required) +
//            Content for approval (.docx, optional) -> portal_gmb_create ->
//            storage uploads under the returned prefix -> portal_gmb_submit.
//            The droplet importer takes it from there (status polls every 10 s).
//   Review   (agency_review) "Preview as client" = the client page, then an
//            editable task list (title, what to do, owner, Needs client OK,
//            Hide) and "Release to client" behind a confirm step.
//   Released progress by owner, the agency can tick any task, the editor
//            stays available, and "Replace with a new audit" starts a fresh
//            upload (the current audit is archived when that upload starts).

const PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const POLL_MS = 10000;

const defaultTitle = (date) => `Google Business Profile review — ${date || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;

// Session 11: the GMB Audit lives in its own tab on the client Settings page.
// The old route (Inbox rows from v_portal_attention, bookmarks) lands there.
export default function AdminGmb() {
  const { clientId } = useParams();
  return <Navigate to={clientPath(`/admin/clients/${clientId}/settings?tab=gmb`, clientId)} replace />;
}

// Rendered by AdminClientSettings for ?tab=gmb.
export function GmbTab({ clientId, clientName }) {
  const [audit, setAudit] = useState(undefined);      // undefined = loading, null = none
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState(null);
  const [replace, setReplace] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [banner, setBanner] = useState(null);

  const load = useCallback(async () => {
    const { data, error: e } = await supabase.from('v_portal_gmb_audit').select('*').eq('client_id', clientId).maybeSingle();
    if (e) { setError(e.message); return; }
    setAudit(data || null);
    if (data) {
      const { data: rows, error: tErr } = await supabase.from('v_portal_gmb_tasks').select('*').eq('audit_id', data.audit_id).order('sort');
      if (tErr) { setError(tErr.message); return; }
      setTasks(rows || []);
    } else {
      setTasks([]);
    }
    setError(null);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // Uploading / processing: refresh every 10 s until the importer lands.
  const inFlight = audit && (audit.status === 'processing' || audit.status === 'uploading');
  useEffect(() => {
    if (!inFlight) return undefined;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [inFlight, load]);

  if (error) return <div className="empty"><h2>Couldn't load the GMB audit</h2><p>{error}</p></div>;
  if (audit === undefined) return <p className="lede">Loading…</p>;

  const showUpload = replace || !audit || audit.status === 'uploading' || audit.status === 'failed';

  async function onToggle(task, done) {
    const stamp = done ? new Date().toISOString() : null;
    setTasks((s) => s.map((t) => (t.task_id === task.task_id ? { ...t, done_at: stamp } : t)));
    const { error: e } = await supabase.rpc('portal_gmb_toggle_task', { p_task_id: task.task_id, p_done: done });
    if (e) {
      setTasks((s) => s.map((t) => (t.task_id === task.task_id ? { ...t, done_at: task.done_at } : t)));
      setBanner({ tone: 'err', text: `Couldn't save that tick: ${e.message}` });
    }
  }

  return (
    <>
      <p className="hint" style={{ marginTop: 0, marginBottom: 14 }}>
        Upload the deck and audit.json from the gmb-audit skill, check exactly what {clientName} will see, fix any task wording or owner, then release.
      </p>
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}

      {audit && audit.status === 'failed' && !replace && (
        <div className="banner banner-err">
          <strong>The last upload failed.</strong> {audit.error || 'No details were recorded.'} Fix the file and upload again below.
        </div>
      )}
      {audit && audit.status === 'uploading' && !replace && (
        <div className="banner banner-waiting">A previous upload didn't finish. Upload the files again; the unfinished one is archived automatically.</div>
      )}

      {showUpload && (
        <UploadCard clientId={clientId} clientName={clientName} replacing={replace && audit && audit.status !== 'failed' && audit.status !== 'uploading' ? audit : null}
                    onCancel={replace ? () => setReplace(false) : null}
                    onSubmitted={async () => { setReplace(false); await load(); }} />
      )}

      {!showUpload && audit.status === 'processing' && (
        <section className="review-section">
          <h2>Processing</h2>
          <Working text="Reading audit.json and converting the deck to PDF. This usually takes a minute or two; the page refreshes on its own." />
          <p className="hint">Started {fmtDateTime(audit.updated_at)}. {audit.title}</p>
        </section>
      )}

      {!showUpload && audit.status === 'agency_review' && (
        <ReviewPanel audit={audit} tasks={tasks} clientName={clientName} onChange={load} onReleased={load} />
      )}

      {!showUpload && audit.status === 'released' && (
        <>
          <ReleasedSummary audit={audit} tasks={tasks} />
          <section className="review-section">
            <div className="section-heading" style={{ margin: '0 0 6px' }}>
              <h2>What the client sees</h2>
              <span className="count">You can tick any task here.</span>
            </div>
            <div className="gmb-preview-frame">
              <GmbAuditView audit={audit} tasks={tasks.filter((t) => !t.hidden)} canTick={() => true} onToggle={onToggle} />
            </div>
          </section>
          <TaskEditor tasks={tasks} onSaved={load} collapsed />
          <section className="review-section">
            <h2>Replace with a new audit</h2>
            <p className="hint" style={{ marginTop: 0 }}>Starts a fresh upload. The current audit and its checklist are archived the moment the new upload begins, and the client sees nothing new until you release it.</p>
            {!confirmReplace ? (
              <button type="button" className="btn" onClick={() => setConfirmReplace(true)}>Replace with a new audit…</button>
            ) : (
              <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
                <button type="button" className="btn btn-danger" onClick={() => { setConfirmReplace(false); setReplace(true); }}>Yes, archive the current audit and upload a new one</button>
                <button type="button" className="btn" onClick={() => setConfirmReplace(false)}>Keep the current audit</button>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function UploadCard({ clientId, clientName, replacing, onCancel, onSubmitted }) {
  const [title, setTitle] = useState(defaultTitle());
  const titleTouched = useRef(false);
  const [deck, setDeck] = useState(null);
  const [json, setJson] = useState(null);
  const [docx, setDocx] = useState(null);
  const [jsonInfo, setJsonInfo] = useState(null);     // { date, issues, name } | { error }
  const [busy, setBusy] = useState(null);             // step text
  const [err, setErr] = useState(null);

  async function onJsonPicked(file) {
    setJson(file); setJsonInfo(null);
    if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      const date = data?.client?.date || null;
      const issues = Array.isArray(data?.issues) ? data.issues.length : 0;
      setJsonInfo({ date, issues, name: data?.client?.name || null });
      if (!titleTouched.current) setTitle(defaultTitle(date));
    } catch (e) {
      setJsonInfo({ error: `This file isn't valid JSON (${e.message}). The importer will refuse it.` });
    }
  }

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    if (!deck || !json) { setErr('The PowerPoint and audit.json are both required.'); return; }
    if (!/\.pptx$/i.test(deck.name)) { setErr('The deck must be a .pptx file.'); return; }
    if (docx && !/\.docx$/i.test(docx.name)) { setErr('The approval document must be a .docx file.'); return; }
    try {
      setBusy('Creating the audit…');
      const { data, error: cErr } = await supabase.rpc('portal_gmb_create', { p_client_id: clientId, p_title: title.trim() || null });
      if (cErr) throw cErr;
      const prefix = data.prefix;
      const put = async (name, file, contentType) => {
        const { error: uErr } = await supabase.storage.from(BUCKET).upload(`${prefix}/${name}`, file, { contentType, upsert: true });
        if (uErr) throw new Error(`${name}: ${uErr.message}`);
      };
      setBusy('Uploading the PowerPoint…');   await put('deck.pptx', deck, PPTX);
      setBusy('Uploading audit.json…');       await put('audit.json', json, 'application/json');
      if (docx) { setBusy('Uploading the approval document…'); await put('approval.docx', docx, DOCX); }
      setBusy('Starting the import…');
      const { error: sErr } = await supabase.rpc('portal_gmb_submit', { p_audit_id: data.audit_id });
      if (sErr) throw sErr;
      setBusy(null);
      await onSubmitted();
    } catch (ex) {
      setBusy(null);
      setErr(`Upload didn't finish: ${ex.message || ex}. Nothing was shown to the client. Fix the problem and upload again.`);
    }
  }

  return (
    <section className="review-section gmb-upload">
      <h2>{replacing ? 'Upload a new audit' : 'Upload the audit'}</h2>
      <p className="hint" style={{ marginTop: 0 }}>
        From the gmb-audit skill: the deck (.pptx) and audit.json are required; the Content for approval .docx is optional.
        {replacing ? ` The current audit "${replacing.title}" is archived as soon as you press Upload and process.` : ''}
      </p>
      <form onSubmit={submit} className="client-form">
        <Field label="Title" hint="What the client sees as the page heading.">
          <input type="text" value={title} onChange={(e) => { titleTouched.current = true; setTitle(e.target.value); }} disabled={!!busy} />
        </Field>
        <Field label="PowerPoint (.pptx), required" hint={deck ? `${deck.name} · ${(deck.size / 1024 / 1024).toFixed(1)} MB` : 'The client can view it as a PDF and download the PowerPoint.'}>
          <input type="file" accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation" onChange={(e) => setDeck(e.target.files?.[0] || null)} disabled={!!busy} />
        </Field>
        <Field label="audit.json, required"
               hint={jsonInfo?.error ? jsonInfo.error : jsonInfo ? `${jsonInfo.issues} issue${jsonInfo.issues === 1 ? '' : 's'}${jsonInfo.name ? ` · ${jsonInfo.name}` : ''}${jsonInfo.date ? ` · ${jsonInfo.date}` : ''}` : 'Every issue becomes a checklist task. The client never sees this file.'}>
          <input type="file" accept=".json,application/json" onChange={(e) => onJsonPicked(e.target.files?.[0] || null)} disabled={!!busy} />
        </Field>
        <Field label="Content for approval (.docx), optional" hint={docx ? docx.name : 'Offered to the client as a download.'}>
          <input type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => setDocx(e.target.files?.[0] || null)} disabled={!!busy} />
        </Field>
        {err && <div className="banner banner-err">{err}</div>}
        {busy && <Working text={busy} />}
        <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="submit" className="btn btn-primary" disabled={!!busy || !deck || !json}>Upload and process</button>
          {onCancel && <button type="button" className="btn" onClick={onCancel} disabled={!!busy}>Cancel</button>}
        </div>
        <p className="hint">Nothing is visible to {clientName} until you release it after review.</p>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------

function ReviewPanel({ audit, tasks, clientName, onChange, onReleased }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const visible = tasks.filter((t) => !t.hidden);
  const hiddenCount = tasks.length - visible.length;

  async function release() {
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc('portal_gmb_release', { p_audit_id: audit.audit_id });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setConfirm(false);
    await onReleased();
  }

  return (
    <>
      <div className="banner banner-gate">
        <strong>Ready for your review.</strong> {clientName} can't see any of this until you press Release to client.
        {' '}{visible.length} task{visible.length === 1 ? '' : 's'} will be shown{hiddenCount ? `, ${hiddenCount} hidden` : ''}.
      </div>
      {audit.error && (
        <div className="banner banner-waiting"><strong>No PDF.</strong> {audit.error} The client will still get the PowerPoint download.</div>
      )}

      <section className="review-section">
        <div className="section-heading" style={{ margin: '0 0 6px' }}>
          <h2>Preview as client</h2>
          <span className="count">Exactly what {clientName} will see</span>
        </div>
        <div className="gmb-preview-frame">
          <div className="gmb-preview-ribbon">Preview · not yet released</div>
          <GmbAuditView audit={audit} tasks={visible} canTick={(t) => t.owner_group === 'client'} onToggle={null} preview />
        </div>
      </section>

      <TaskEditor tasks={tasks} onSaved={onChange} />

      <section className="review-section">
        <h2>Release to client</h2>
        <p className="hint" style={{ marginTop: 0 }}>Makes the review, the checklist and the downloads visible to {clientName} and puts an item in their Inbox. No email is sent.</p>
        {err && <div className="banner banner-err">{err}</div>}
        <button type="button" className="btn btn-primary" onClick={() => setConfirm(true)} disabled={busy}>Release to client</button>
      </section>

      {confirm && (
        <div className="modal-backdrop" onClick={() => !busy && setConfirm(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="gmb-release-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="gmb-release-title">Release to {clientName}?</h2>
            <p>{visible.length} task{visible.length === 1 ? '' : 's'} go live{hiddenCount ? ` (${hiddenCount} stay hidden)` : ''}. The client can tick the tasks tagged "You" and download the deck{audit.approval_path ? ' and the approval document' : ''}.</p>
            <div className="review-actions">
              <button type="button" className="btn" onClick={() => setConfirm(false)} disabled={busy}>Not yet</button>
              <button type="button" className="btn btn-primary" onClick={release} disabled={busy}>{busy ? 'Releasing…' : 'Yes, release'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function ReleasedSummary({ audit, tasks }) {
  const visible = tasks.filter((t) => !t.hidden);
  const by = useMemo(() => ['client', 'agency', 'developer'].map((g) => {
    const list = visible.filter((t) => t.owner_group === g);
    return [g, list.filter((t) => t.done_at).length, list.length];
  }).filter(([, , n]) => n > 0), [visible]);
  const done = visible.filter((t) => t.done_at).length;
  return (
    <div className="banner banner-ok gmb-released">
      <strong>Released {fmtDateTime(audit.released_at)}.</strong>{' '}
      {done} of {visible.length} done · {by.map(([g, d, n]) => `${OWNER_LABEL[g]} ${d} of ${n}`).join(' · ')}
      {' · '}{audit.client_first_viewed_at ? `Client first opened it ${fmtDateTime(audit.client_first_viewed_at)}.` : 'The client has not opened it yet.'}
    </div>
  );
}

// ---------------------------------------------------------------------------

function TaskEditor({ tasks, onSaved, collapsed = false }) {
  const [open, setOpen] = useState(!collapsed);
  const ordered = useMemo(() => [...tasks].sort((a, b) => a.sort - b.sort), [tasks]);
  return (
    <section className="review-section">
      <div className="section-heading" style={{ margin: '0 0 6px' }}>
        <h2>Edit tasks</h2>
        {collapsed && <button type="button" className="link-btn" onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Show'}</button>}
      </div>
      <p className="hint" style={{ marginTop: 0 }}>Change the wording or the owner, flag "Needs client OK", or hide a task from the client. Each row saves on its own.</p>
      {open && (
        <ul className="gmb-edit-list">
          {ordered.map((t) => <TaskEditRow key={t.task_id} task={t} onSaved={onSaved} />)}
        </ul>
      )}
    </section>
  );
}

function TaskEditRow({ task, onSaved }) {
  const fresh = (t) => ({ title: t.title || '', fix: t.fix || '', owner_group: t.owner_group, needs_client_approval: !!t.needs_client_approval, hidden: !!t.hidden });
  const [d, setD] = useState(() => fresh(task));
  const [busy, setBusy] = useState(false);
  const saved = useSaveStatus();   // Session 24: the shared Saved / Couldn't save line
  useEffect(() => { setD(fresh(task)); }, [task.task_id, task.edited_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const base = fresh(task);
  const changed = Object.fromEntries(Object.entries(d).filter(([k, v]) => v !== base[k]));
  const dirty = Object.keys(changed).length > 0;

  async function save() {
    setBusy(true); saved.start();
    const { error } = await supabase.rpc('portal_gmb_edit_task', { p_task_id: task.task_id, p_fields: changed });
    setBusy(false);
    if (error) { saved.fail(error.message); return; }
    saved.ok();
    await onSaved();
  }

  return (
    <li className={`gmb-edit-row ${d.hidden ? 'is-hidden' : ''}`}>
      <div className="gmb-edit-meta">
        <span className="plan-card-type">#{task.n}</span>
        {task.priority && <span className={`gmb-chip gmb-prio prio-${String(task.priority).toLowerCase()}`}>{task.priority}</span>}
        {task.owner_label && <span className="muted">Deck says: “{task.owner_label}”</span>}
        {task.done_at && <span className="gmb-chip gmb-done">Done</span>}
      </div>
      <div className="gmb-edit-fields">
        <label className="field">
          <span className="field-label">Title</span>
          <input type="text" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} disabled={busy} />
        </label>
        <label className="field">
          <span className="field-label">What to do</span>
          <textarea rows={3} value={d.fix} onChange={(e) => setD({ ...d, fix: e.target.value })} disabled={busy} />
        </label>
        <div className="inline-fields">
          <label className="field">
            <span className="field-label">Owner</span>
            <select value={d.owner_group} onChange={(e) => setD({ ...d, owner_group: e.target.value })} disabled={busy}>
              <option value="client">{OWNER_LABEL.client} (client)</option>
              <option value="agency">{OWNER_LABEL.agency}</option>
              <option value="developer">{OWNER_LABEL.developer}</option>
            </select>
          </label>
          <div className="gmb-edit-toggles">
            <Toggle checked={d.needs_client_approval} onChange={(v) => setD({ ...d, needs_client_approval: v })} label="Needs client OK" disabled={busy} />
            <Toggle checked={d.hidden} onChange={(v) => setD({ ...d, hidden: v })} label="Hide from client" disabled={busy} />
          </div>
        </div>
        <div className="review-actions" style={{ justifyContent: 'flex-start', marginTop: 0 }}>
          <button type="button" className="btn btn-primary" onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</button>
          {dirty && <button type="button" className="link-btn" onClick={() => setD(base)} disabled={busy}>Undo</button>}
          <SaveStatus status={saved.status} />
        </div>
      </div>
    </li>
  );
}
